import { useQueryClient } from "@tanstack/react-query";
import type { TaskSocketMessage } from "@wms/domain";
import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/client";
import { useAuthStore } from "../auth/store";

function socketUrl(token: string): string {
  const base = API_BASE.startsWith("http") ? new URL(API_BASE) : new URL(API_BASE, window.location.origin);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = base.pathname.replace(/\/$/, "") + "/ws/tasks";
  base.search = `?token=${encodeURIComponent(token)}`;
  return base.toString();
}

/** Keeps one WebSocket per ARM session and refreshes task queries on every message. Reconnects with backoff. */
export function useTaskSocket(enabled: boolean) {
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);
  const [connected, setConnected] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!enabled || !token) return;
    let socket: WebSocket | null = null;
    let timer: number | undefined;
    let closed = false;

    const connect = () => {
      socket = new WebSocket(socketUrl(token));
      socket.onopen = () => {
        attempts.current = 0;
        setConnected(true);
      };
      socket.onmessage = (event) => {
        const message = JSON.parse(event.data) as TaskSocketMessage;
        if (message.channel !== "tasks") return;
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
        queryClient.invalidateQueries({ queryKey: ["task_events"] });
        queryClient.invalidateQueries({ queryKey: ["stock"] });
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        if (message.task?.id) queryClient.invalidateQueries({ queryKey: ["tasks", message.task.id] });
      };
      socket.onclose = (event) => {
        setConnected(false);
        if (closed || event.code === 4401 || event.code === 4403) return;
        attempts.current += 1;
        timer = window.setTimeout(connect, Math.min(30000, 1000 * 2 ** attempts.current));
      };
    };
    connect();
    const ping = window.setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send("ping"), 25000);
    return () => {
      closed = true;
      window.clearInterval(ping);
      window.clearTimeout(timer);
      socket?.close();
    };
  }, [enabled, token, queryClient]);

  return connected;
}
